import { useCallback, FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, Button, useThemeValue, Svg } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useDialog, useNotification } from '@/_shared/hooks';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getEmployeesImportsOverviewCall, postEmployeesImportsAbortByJobIdCall } from '../../state';

export const DialogImportEmployeesStillRunning: FC = () => {
  const t = useTranslations('people.dialogImportEmployeesStillRunning');
  const sharedT = useTranslations();
  const { notify } = useNotification();

  const navigate = useNavigate();
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();
  const gapL = convertSizeToNumber(`${useThemeValue('sizes', 'l')}`);
  const { openDialog, closeDialog, Dialog, isOpen } = useDialog(
    'dialogImportEmployeesStillRunning',
  );
  const { execute: executeGetEmployeesImportsOverview, jobStillRunning } =
    getEmployeesImportsOverviewCall.hook();
  const {
    execute: executePostEmployeesImportsAbortByJobId,
    loading: loadingPostEmployeesImportsAbortByJobId,
  } = postEmployeesImportsAbortByJobIdCall.hook();

  const goToHomePage = useCallback(() => {
    navigate(Routes.Homepage);
  }, [navigate]);

  const onAbort = useCallback(() => {
    executePostEmployeesImportsAbortByJobId({
      jobId: jobStillRunning?.pendingJob || '',
      successSaga: () => {
        notify(sharedT('success.genericApiSuccess'), 'success');
        closeDialog();
      },
      errorSaga: () => {
        notify(sharedT('errors.genericApiError'), 'error');
      },
    });
  }, [
    closeDialog,
    executePostEmployeesImportsAbortByJobId,
    jobStillRunning?.pendingJob,
    notify,
    sharedT,
  ]);

  useEffect(() => {
    if (jobStillRunning) {
      openDialog();
    }
  }, [openDialog, jobStillRunning]);

  useEffect(() => {
    executeGetEmployeesImportsOverview({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      avoidCloseOnBackDrop
      closeButton={false}
      maxWidth='700px'
    >
      <Box display='flex' mx='l' flexDirection='column' px='s'>
        <Typography variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>
      <Box display='flex' mt='l' flexDirection='column' px='s'>
        <Typography variant='p2/light' color='primary/text' textAlign='center'>
          {t('description')}
        </Typography>
      </Box>

      <Box
        display='flex'
        mx='l'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        px='s'
        style={{ gap: isDesktop ? gapL : 0 }}
        justifyContent='center'
      >
        <Button
          loading={loadingPostEmployeesImportsAbortByJobId}
          spinnerColor='white'
          variant='link/primary'
          onClick={onAbort}
          mt='l'
          style={{ textDecoration: 'none' }}
        >
          <Svg name='close' fill='primary/text' size='s' mr='xs' />
          {t('cancel')}
        </Button>

        <Button spinnerColor='white' variant='primary' onClick={goToHomePage} mt='l'>
          {t('confirm')}
        </Button>
      </Box>
    </Dialog>
  );
};
